import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import HttpManager from '../utils/HttpManager';


function ForgotPassword() {

    const navigate = useNavigate();

    /*
        step 1 = Enter Email
        step 2 = Verify OTP
        step 3 = Reset Password
    */
    const [step, setStep] = useState(1);

    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');

    /*
        We don't use the OTP directly for resetting password.

        Once OTP verifies, backend returns a short-lived ResetToken.

        ResetToken must then be submitted when changing password.
    */
    const [resetToken, setResetToken] = useState('');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);


    const isValidEmail = (value) => {
        return /^\S+@\S+\.\S+$/.test(value);
    };


    /*
        STEP 1
        Request password reset OTP
    */
    const requestOtp = async () => {

        const normalizedEmail = email.trim();

        const newErrors = {};


        if (!normalizedEmail) {

            newErrors.email = 'User email is required';

        }
        else if (!isValidEmail(normalizedEmail)) {

            newErrors.email = 'Enter a valid email address';

        }


        setErrors(newErrors);


        if (Object.keys(newErrors).length > 0) {
            return;
        }


        setLoading(true);


        try {

            const response =
                await new HttpManager(navigate)
                    .post(
                        '/User/ForgotPassword',
                        {
                            Email: normalizedEmail
                        }
                    );


            if (response.IsSuccess === true) {

                setOtp('');

                setErrors({});

                setStep(2);


                Swal.fire({
                    title: 'Check your email',
                    text:
                        response.Message ||
                        'If an account exists for this email, an OTP has been sent.',
                    icon: 'success'
                });

            }
            else {

                Swal.fire({
                    title: 'Unable to send OTP',
                    text:
                        response.Message ||
                        'Please retry after sometime.',
                    icon: 'error'
                });

            }

        }
        catch (error) {

            Swal.fire({
                title: 'Unable to send OTP',
                text:
                    'Request failed due to a technical error. Please retry after sometime.',
                icon: 'error'
            });

        }
        finally {

            setLoading(false);

        }
    };


    /*
        STEP 2
        Verify OTP
    */
    const verifyOtp = async () => {

        const newErrors = {};

        const normalizedOtp = otp.trim();


        if (!normalizedOtp) {

            newErrors.otp = 'OTP is required';

        }
        else if (!/^\d{6}$/.test(normalizedOtp)) {

            newErrors.otp = 'OTP must be 6 digits';

        }


        setErrors(newErrors);


        if (Object.keys(newErrors).length > 0) {
            return;
        }


        setLoading(true);


        try {

            const response =
                await new HttpManager(navigate)
                    .post(
                        '/User/VerifyPasswordResetOtp',
                        {
                            Email: email.trim(),
                            Otp: normalizedOtp
                        }
                    );


            if (response.IsSuccess === true) {

                /*
                    Depending on how your HttpManager / JSON serializer
                    returns property names, support both.
                */
                const token =
                    response.Data?.ResetToken ||
                    response.Data?.resetToken;


                if (!token) {

                    Swal.fire({
                        title: 'OTP verification failed',
                        text: 'Reset token was not returned by server.',
                        icon: 'error'
                    });

                    return;
                }


                setResetToken(token);

                setErrors({});

                setStep(3);

            }
            else {

                Swal.fire({
                    title: 'OTP verification failed',
                    text:
                        response.Message ||
                        'The OTP is invalid or expired.',
                    icon: 'error'
                });

            }

        }
        catch (error) {

            console.error(error);

            Swal.fire({
                title: 'OTP verification failed',
                text:
                    'Verification failed due to a technical error. Please retry.',
                icon: 'error'
            });

        }
        finally {

            setLoading(false);

        }
    };


    /*
        STEP 3
        Set new password
    */
    const resetPassword = async () => {

        const newErrors = {};


        if (!newPassword) {

            newErrors.newPassword =
                'New password is required';

        }
        else if (newPassword.length < 8) {

            newErrors.newPassword =
                'Password must be at least 8 characters';

        }


        if (!confirmPassword) {

            newErrors.confirmPassword =
                'Confirm password is required';

        }
        else if (newPassword !== confirmPassword) {

            newErrors.confirmPassword =
                'Passwords do not match';

        }


        setErrors(newErrors);


        if (Object.keys(newErrors).length > 0) {
            return;
        }


        setLoading(true);


        try {

            const response =
                await new HttpManager(navigate)
                    .post(
                        '/User/ResetPassword',
                        {
                            Email: email.trim(),

                            ResetToken: resetToken,

                            NewPassword: newPassword,

                            ConfirmPassword: confirmPassword
                        }
                    );


            if (response.IsSuccess === true) {

                await Swal.fire({
                    title: 'Password reset successful',
                    text:
                        response.Message ||
                        'Your password has been changed. You can now log in.',
                    icon: 'success'
                });


                navigate('/Login');

            }
            else {

                Swal.fire({
                    title: 'Password reset failed',
                    text:
                        response.Message ||
                        'Unable to reset password. Please request a new OTP.',
                    icon: 'error'
                });

            }

        }
        catch (error) {

            console.error(error);

            Swal.fire({
                title: 'Password reset failed',
                text:
                    'Password reset failed due to a technical error. Please retry.',
                icon: 'error'
            });

        }
        finally {

            setLoading(false);

        }
    };


    return (

        <div className="d-flex justify-content-center align-items-center vh-100 w-100">

            <main className="form-signin w-100 m-auto">

                <form>

                    {/* Logo */}
                    <div className="text-center mb-4">

                        <img
                            className="mb-4"
                            src="/DecipherX_Logo1.svg"
                            alt="decipherX Logo"
                            width="102"
                            height="87"
                        />

                        <h5 className="fw-normal">
                            Forgot Password
                        </h5>

                    </div>


                    {/* ============================= */}
                    {/* STEP 1 - ENTER EMAIL */}
                    {/* ============================= */}

                    {
                        step === 1 &&
                        (
                            <>

                                <p className="text-body-secondary">

                                    Enter your registered email address.
                                    We will send a 6-digit OTP to your email.

                                </p>


                                <div className="form-floating my-3">

                                    <input
                                        type="email"
                                        className={`form-control ${
                                            errors.email
                                                ? 'is-invalid'
                                                : ''
                                        }`}
                                        id="forgotPasswordEmail"
                                        placeholder="User Email"
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(e.target.value)
                                        }
                                        autoComplete="email"
                                    />

                                    <label htmlFor="forgotPasswordEmail">
                                        User Email
                                    </label>


                                    {
                                        errors.email &&
                                        <div className="invalid-feedback">

                                            {errors.email}

                                        </div>
                                    }

                                </div>


                                <button
                                    className="btn btn-primary w-100 py-2"
                                    type="button"
                                    onClick={requestOtp}
                                    disabled={loading}
                                >

                                    {
                                        loading
                                            ? 'Sending...'
                                            : 'Send OTP'
                                    }

                                </button>

                            </>
                        )
                    }



                    {/* ============================= */}
                    {/* STEP 2 - VERIFY OTP */}
                    {/* ============================= */}

                    {
                        step === 2 &&
                        (
                            <>

                                <p className="text-body-secondary">

                                    Enter the 6-digit OTP sent to

                                    {' '}

                                    <strong>
                                        {email}
                                    </strong>.

                                </p>


                                <div className="form-floating my-3">

                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        maxLength="6"
                                        className={`form-control ${
                                            errors.otp
                                                ? 'is-invalid'
                                                : ''
                                        }`}
                                        id="passwordResetOtp"
                                        placeholder="OTP"
                                        value={otp}
                                        onChange={(e) =>
                                            setOtp(
                                                e.target.value
                                                    .replace(/\D/g, '')
                                                    .slice(0, 6)
                                            )
                                        }
                                        autoComplete="one-time-code"
                                    />

                                    <label htmlFor="passwordResetOtp">
                                        OTP
                                    </label>


                                    {
                                        errors.otp &&
                                        <div className="invalid-feedback">

                                            {errors.otp}

                                        </div>
                                    }

                                </div>


                                <button
                                    className="btn btn-primary w-100 py-2"
                                    type="button"
                                    onClick={verifyOtp}
                                    disabled={loading}
                                >

                                    {
                                        loading
                                            ? 'Verifying...'
                                            : 'Verify OTP'
                                    }

                                </button>


                                <div className="d-flex justify-content-between mt-3">

                                    {/* Change email */}
                                    <button
                                        className="btn btn-link p-0 text-decoration-none"
                                        type="button"
                                        disabled={loading}
                                        onClick={() => {

                                            setStep(1);

                                            setOtp('');

                                            setErrors({});

                                        }}
                                    >
                                        Change email
                                    </button>


                                    {/* Resend OTP */}
                                    <button
                                        className="btn btn-link p-0 text-decoration-none"
                                        type="button"
                                        onClick={requestOtp}
                                        disabled={loading}
                                    >
                                        Resend OTP
                                    </button>

                                </div>

                            </>
                        )
                    }



                    {/* ============================= */}
                    {/* STEP 3 - RESET PASSWORD */}
                    {/* ============================= */}

                    {
                        step === 3 &&
                        (
                            <>

                                <p className="text-body-secondary">

                                    OTP verified.
                                    Enter your new password.

                                </p>


                                {/* New password */}
                                <div className="form-floating my-3">

                                    <input
                                        type="password"
                                        className={`form-control ${
                                            errors.newPassword
                                                ? 'is-invalid'
                                                : ''
                                        }`}
                                        id="newPassword"
                                        placeholder="New Password"
                                        value={newPassword}
                                        onChange={(e) =>
                                            setNewPassword(e.target.value)
                                        }
                                        autoComplete="new-password"
                                    />

                                    <label htmlFor="newPassword">
                                        New Password
                                    </label>


                                    {
                                        errors.newPassword &&
                                        <div className="invalid-feedback">

                                            {errors.newPassword}

                                        </div>
                                    }

                                </div>


                                {/* Confirm password */}
                                <div className="form-floating my-3">

                                    <input
                                        type="password"
                                        className={`form-control ${
                                            errors.confirmPassword
                                                ? 'is-invalid'
                                                : ''
                                        }`}
                                        id="confirmNewPassword"
                                        placeholder="Confirm Password"
                                        value={confirmPassword}
                                        onChange={(e) =>
                                            setConfirmPassword(
                                                e.target.value
                                            )
                                        }
                                        autoComplete="new-password"
                                    />

                                    <label htmlFor="confirmNewPassword">
                                        Confirm Password
                                    </label>


                                    {
                                        errors.confirmPassword &&
                                        <div className="invalid-feedback">

                                            {errors.confirmPassword}

                                        </div>
                                    }

                                </div>


                                <button
                                    className="btn btn-primary w-100 py-2"
                                    type="button"
                                    onClick={resetPassword}
                                    disabled={loading}
                                >

                                    {
                                        loading
                                            ? 'Resetting...'
                                            : 'Reset Password'
                                    }

                                </button>

                            </>
                        )
                    }


                    {/* Back to Login */}
                    <div className="text-center my-3">

                        <Link
                            to="/Login"
                            style={{
                                textDecoration: 'none'
                            }}
                        >
                            Back to Login
                        </Link>

                    </div>


                    <p className="mt-5 mb-3 text-body-secondary">

                        decipherX © 2026

                    </p>

                </form>

            </main>

        </div>
    );
}


export default ForgotPassword;